import Agreement from 'agreement/classes/Agreement';
import { findActiveAgreementForObject, getActiveAgreementObjects } from 'serviceRequest/utils/activeAgreementObjects';
import { NotificationAlert } from 'core/classes/NotificationAlert';
import { coreActions } from 'core/redux/actions/coreActions';
import { DateTime } from 'luxon';
import useQueryServiceRequest from '../../api/gql/useQueryServiceRequest';
import { RentalObjectSelectData } from 'rentalObject/types/rentalObjects';
import { QueryStatusData } from 'core/hooks/useQueryHelper';
import { RentalObject, ServiceData, ServiceRequest } from '../../types/serviceRequest';
import { RowData } from 'core/components/documentItems/DocumentDetails';
import { APP_LUX_DATE_TIME_SHORT_FORMAT, DB_LUX_DATE_TIME_FORMAT } from 'core/utils/datesHelper';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'core/redux/rootReducer';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
    addServiceRequestFiles,
    changeServiceRequestFieldValue,
    resetServiceRequestFormValues,
    selectRentalObjectValue,
} from '../../redux/serviceRequestActions';
import useMutationMakeServiceRequest from '../../api/gql/useMutationMakeServiceRequest';
import { ApolloError } from 'apollo-boost';
import { AxiosError } from 'axios';
import { FiltersState } from 'core/types/redux/filtersState';
import { ConfirmationCallback } from '../../../AI/types/conversation_callback';

export const MAX_REQUEST_TITLE_LENGTH = 200;

interface IFormErrors {
    allFieldsRequired: boolean;
    validRequestTitleLength: boolean;
}

const useServiceRequestController = (
    confirmationCallback: ConfirmationCallback | null = null,
): ServiceRequestControllerReturnType => {
    const [selectedFiles, setFiles] = useState<File[] | null>(null);

    const {
        t,
        i18n: { language },
    } = useTranslation();

    const selectedObjectID = useSelector(({ agreementObjects }: AppState) => agreementObjects.selectedObjectID);
    const objects = useSelector(({ agreementObjects }: AppState) => agreementObjects.objects);
    const filters = useSelector(({ filters }: AppState) => filters);
    const agreements = useSelector(({ agreements }: AppState) => agreements.map);

    const [selectedRentalObjectID, setSelectedRentalObjectID] = useState(selectedObjectID);

    const {
        serviceRequests: serviceRequestsData,
        loading,
        error,
        getServiceRequests,
        lazyServiceRequests,
    } = useQueryServiceRequest();

    const serviceRequests = useMemo(() => {
        return serviceRequestsData
            ? serviceRequestsData.map((item) => ({ ...item, meta: { id: item.id } }))
            : serviceRequestsData;
    }, [serviceRequestsData]);

    const dispatch = useDispatch();

    const handleSelectChange = (event: ChangeEvent<{ value: unknown; name?: unknown }>) => {
        const value = event.target?.value as string;
        setSelectedRentalObjectID(() => (value ? Number(value) : null));
        dispatch(selectRentalObjectValue(event));
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(changeServiceRequestFieldValue(event));
    };

    const handleOnFileSelect = (files: File[]) => {
        setFiles(files);
        dispatch(addServiceRequestFiles(files));
    };

    const requestForm = useSelector(({ serviceRequests }: AppState) => serviceRequests.requestForm);
    const {
        createServiceRequest,
        data,
        error: serviceRequestError,
        loading: serviceRequestLoading,
        called,
        createRequest,
        uploading,
        createServiceRequestWithoutUpdateChannels,
    } = useMutationMakeServiceRequest(confirmationCallback);

    const checkErrorsInForm = (requestForm: RequestForm): IFormErrors | null => {
        const allFieldsRequired = Object.values(requestForm).every((fieldValue) => !!fieldValue);
        const validRequestTitleLength = requestForm?.requestTitle?.length <= MAX_REQUEST_TITLE_LENGTH;

        if (allFieldsRequired && validRequestTitleLength) return null;

        return { allFieldsRequired, validRequestTitleLength };
    };

    const showErrors = (errorsMap: IFormErrors | null) => {
        for (const errorKey in errorsMap) {
            if (!errorsMap[errorKey as keyof IFormErrors]) {
                dispatch(
                    coreActions.notification.set(
                        new NotificationAlert({
                            message: t(`notices.${errorKey}`, { length: MAX_REQUEST_TITLE_LENGTH }),
                            level: NotificationAlert.WARNING,
                        }),
                    ),
                );
            }
        }
    };

    const handleSubmitForm = () => {
        const errorsMap = checkErrorsInForm(requestForm);
        if (errorsMap) {
            showErrors(errorsMap);

            return false;
        }

        const objectId = Number(requestForm.rentalObjectSelect);
        const agreement =
            findActiveAgreementForObject(agreements, objectId) ||
            Object.values(agreements).find((item) => Agreement.hasObject(item, objectId));
        const rentalObjectUuid = getActiveAgreementObjects(agreements).get(objectId)?.uuid;

        const serviceData: ServiceData = {
            rentalAgreementUuid: agreement?.uuid || '',
            rentalObjectId: objectId,
            serviceTypeId: Number(requestForm.serviceType),
            title: requestForm.requestTitle,
            description: requestForm.requestDescription,
            files: null,
            ...(confirmationCallback && rentalObjectUuid ? { rentalObjectUuid } : {}),
        };

        if (confirmationCallback) {
            createServiceRequestWithoutUpdateChannels({ files: selectedFiles ?? [], serviceData });
        } else {
            if (selectedFiles) {
                createRequest({ files: selectedFiles, serviceData });
            } else {
                createServiceRequest(serviceData);
            }
        }
    };

    useEffect(() => {
        if (error || loading || !data) {
            return;
        }

        if (data.createServiceRequestV2 || data.createServiceRequestV3) {
            dispatch(
                coreActions.notification.set(
                    new NotificationAlert({
                        message: t('notices.isDone'),
                        level: NotificationAlert.SUCCESS,
                    }),
                ),
            );
            dispatch(resetServiceRequestFormValues());
        }
    }, [data, loading, error, dispatch, t]);

    const serviceRequestById = (id: string) => {
        if (!serviceRequests) return null;

        const service = serviceRequests.filter((item: ServiceRequest) => +id === +item.id);
        return service.length > 0 ? service[0] : null;
    };

    const serviceDetailsFor = (service: ServiceRequest): Array<RowData & { columns: 6 | 12 }> => {
        if (!service) return [];

        const customTitlesMap = service.serviceRequestType.titles.reduce<Record<string, string>>(
            (acc, { text, langCode }) => {
                acc[langCode] = text;
                return acc;
            },
            {},
        );

        const serviceTranslationKey = 'pages.service.';
        const fields = ['rentalObject', 'submitDate', 'status', 'type', 'description'];

        return fields.map((name) => {
            let columns: 6 | 12 = 6;

            if (!(name in service && service[name as keyof ServiceRequest]))
                return {
                    labelText: t(serviceTranslationKey + name),
                    primaryText: '',
                    columns,
                };

            let serviceFieldValue = '' + service[name as keyof ServiceRequest];

            switch (name) {
                case 'rentalObject': {
                    const rentalObject: RentalObject = service[name as keyof ServiceRequest] as RentalObject;
                    serviceFieldValue = `${rentalObject.street} ${rentalObject.houseNumber}-${
                        rentalObject.roomNumber ?? rentalObject.apartmentNumber
                    }, ${rentalObject.city}`;
                    break;
                }
                case 'type':
                    if (service.serviceRequestType.isDefault) {
                        serviceFieldValue = serviceFieldValue?.trim()
                            ? t(`maintenanceServices.types.${serviceFieldValue}`)
                            : '';
                    } else {
                        serviceFieldValue = customTitlesMap?.[language] || customTitlesMap?.['en'];
                    }
                    break;
                case 'status':
                    serviceFieldValue = serviceFieldValue?.trim()
                        ? t(`maintenanceServices.statuses.${serviceFieldValue}`)
                        : '';
                    break;
                case 'submitDate':
                    serviceFieldValue = serviceFieldValue?.trim()
                        ? `date:${DateTime.fromFormat(serviceFieldValue, DB_LUX_DATE_TIME_FORMAT).toFormat(
                              APP_LUX_DATE_TIME_SHORT_FORMAT,
                          )}`
                        : '';
                    break;
                case 'files': {
                    serviceFieldValue = '';
                    columns = 12;
                    break;
                }
                case 'description': {
                    columns = 12;
                    break;
                }
            }
            return {
                labelText: t(serviceTranslationKey + name),
                primaryText: serviceFieldValue,
                columns,
            };
        });
    };

    return {
        handleSelectChange,
        handleInputChange,
        handleSubmitForm,
        serviceRequestById,
        serviceRequests,
        loading,
        error: serviceRequestError || error,
        //@ts-ignore
        rentalObjectsData: (objects && Object.values(objects)) || [],
        loadingObjects: false,
        serviceDetailsFor,
        selectedRentalObjectID,
        serviceRequestError,
        serviceRequestLoading,
        data,
        getServiceRequests,
        called,
        lazyServiceRequests,
        files: requestForm.files || [],
        selectedFiles,
        handleOnFileSelect,
        uploading,
        filters,
    };
};

export default useServiceRequestController;

type ServiceRequestControllerReturnType = QueryStatusData & {
    handleSelectChange: (event: ChangeEvent<{ value: unknown; name?: unknown }>) => void;
    handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleSubmitForm: () => void;
    serviceRequests: ServiceRequest[];
    rentalObjectsData: RentalObjectSelectData[];
    loadingObjects: boolean;
    serviceRequestById: (id: string) => ServiceRequest | null;
    serviceDetailsFor: (service: ServiceRequest) => Array<RowData & { columns: 6 | 12 }>;
    selectedRentalObjectID: number | null;
    serviceRequestLoading: boolean;
    serviceRequestError?: ApolloError | AxiosError;
    data?: { createServiceRequestV2?: { id: string }; createServiceRequestV3?: { id: string } };
    getServiceRequests: () => void;
    called: boolean;
    lazyServiceRequests: ServiceRequest[];
    files: File[];
    selectedFiles: File[] | null;
    handleOnFileSelect: (event: File[]) => void;
    uploading: number;
    filters: FiltersState;
    requestForm: RequestForm;
};

export type RequestForm = {
    rentalObjectSelect: string;
    serviceType: string;
    requestTitle: string;
    requestDescription: string;
    files: File[];
};
